import { useState, useEffect } from 'react';
import { HashRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './pages/login/login.js';
import Auth0Callback from './pages/auth0-callback/auth0Callback.js';
import RegistroAlumno from './pages/registro-alumno/registroAlumno.js';
import ProtectedRoute from './pages/protectedRoute/protectedRoute.js';
import MainLayout from './pages/layout/layout.js';
import AdministracionCategorias from './pages/administracion-categorias/administracionCategorias.js';
import AdministracionCanchas from './pages/administracion-canchas/administracionCanchas.js';
import AdministracionProfesores from './pages/administracion-profesores/administracionProfesores.js';
import AdministracionPlanes from './pages/administracion-planes/administracionPlanes.js';
import AdministracionAlumnos from './pages/administracion-alumnos/administracionAlumnos.js';
import SolicitudesNuevosAlumnos from './pages/solicitudes-nuevos-alumnos/solicitudesNuevosAlumnos.js';
import SolicitudesClaseAlumno from './pages/solicitudes-clase-alumno/solicitudesClaseAlumno.js';
import AgendaCalendario from './pages/agenda-calendario/agendaCalendario.js';
import RegistroPagos from './pages/registro-pagos/registroPagos.js';
import DescuentosCreditos from './pages/descuentos-creditos/descuentosCreditos.js';
import DescuentoManual from './pages/descuento-manual/descuentoManual.js';
import AlumnoPerfil from './pages/alumno-perfil/alumnoPerfil.js';
import AlumnoCreditos from './pages/alumno-creditos/alumnoCreditos.js';
import AlumnoHistorialPagos from './pages/alumno-historial-pagos/alumnoHistorialPagos.js';
import AlumnoClases from './pages/alumno-clases/alumnoClases.js';
import AlumnoSolicitudClase from './pages/alumno-solicitud-clase/alumnoSolicitudClase.js';
import Dashboard from './pages/dashboard/dashboard.js';
import ConfiguracionBot from './pages/configuracion-bot/configuracionBot.js';
import AsistenciaBot from './pages/asistencia-bot/asistenciaBot.js';
import { LoginProvider } from './context/loginContext.js';
import { ConfigProvider, theme } from 'antd';
import winpadelLogo from './assets/winpadel.png';

// Componente para manejar el callback de Auth0 en la raíz
const Auth0RootHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar si hay un código de Auth0 en los query params
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (code || error) {
      // Si hay código o error, redirigir a la ruta de callback
      console.log('🔍 Código de Auth0 detectado en raíz, redirigiendo a /auth0-callback');
      navigate(`/auth0-callback?${window.location.search}`, { replace: true });
    } else {
      // Si no hay código, redirigir al login
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  return null; // No renderiza nada, solo maneja la redirección
};

function App() {
  const [themeConfig] = useState({
    colorPrimary: '#666666',
    borderRadius: 4,
    Button: {
      colorPrimary: '#666666',
      algorithm: true,
    },
  });

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: themeConfig.colorPrimary,
          borderRadius: themeConfig.borderRadius,
          colorBgBase: '#1a1a1a',
          colorBgContainer: '#2d2d2d',
          colorBgElevated: '#3a3a3a',
          colorText: '#e0e0e0',
          colorTextSecondary: '#b0b0b0',
          colorBorder: '#404040',
          colorBorderSecondary: '#4a4a4a',
        },
        components: {
          Button: {
            colorPrimary: themeConfig.Button?.colorPrimary,
            algorithm: themeConfig.Button?.algorithm,
          },
          Card: {
            colorBgContainer: '#2d2d2d',
            colorBorderSecondary: '#404040',
          },
          Table: {
            colorBgContainer: '#2d2d2d',
            colorBorderSecondary: '#404040',
            colorTextHeading: '#ffffff',
            colorText: '#e0e0e0',
          },
          Input: {
            colorBgContainer: '#2d2d2d',
            colorBorder: '#404040',
            colorText: '#e0e0e0',
          },
          Select: {
            colorBgContainer: '#2d2d2d',
            colorBorder: '#404040',
            colorText: '#e0e0e0',
          },
          Modal: {
            colorBgElevated: '#2d2d2d',
            colorText: '#e0e0e0',
          },
          Drawer: {
            colorBgElevated: '#2d2d2d',
            colorText: '#e0e0e0',
          },
          Menu: {
            colorBgContainer: '#2d2d2d',
            colorText: '#e0e0e0',
            colorItemBg: '#2d2d2d',
            colorItemText: '#e0e0e0',
            colorItemTextSelected: '#ffffff',
            colorItemBgSelected: '#404040',
            // Submenú popup
            subMenuItemBg: '#2d2d2d',
            itemBg: '#2d2d2d',
            popupBg: '#2d2d2d',
            darkSubMenuItemBg: '#2d2d2d',
            darkItemBg: '#2d2d2d',
            darkPopupBg: '#2d2d2d',
            // Hover states
            itemHoverBg: '#404040',
            darkItemHoverBg: '#404040',
            horizontalItemHoverBg: '#404040',
          },
        },
      }}
    >
      <LoginProvider>
        <Router>
          <Routes>
            {/* Manejar callback de Auth0 en la raíz */}
            <Route path="/" element={<Auth0RootHandler />} />
            
            {/* Login */}
            <Route path="/login" element={<Login />} />
            
            {/* Callback de Auth0 */}
            <Route path="/auth0-callback" element={<Auth0Callback />} />
            
            {/* Registro público de alumno */}
            <Route path="/registro-alumno" element={<RegistroAlumno />} />
            
            {/* Rutas protegidas */}
            <Route
              path="/home-administrador"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <MainLayout>
                    <div style={{ 
                      padding: '20px',
                      minHeight: 'calc(100vh - 64px)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <img 
                          src={winpadelLogo} 
                          alt="WinPadel" 
                          style={{ 
                            maxWidth: window.innerWidth <= 768 ? '200px' : '300px', 
                            height: 'auto', 
                            width: '100%',
                          }} 
                        />
                      </div>
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home-alumno"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <MainLayout>
                    <div style={{ 
                      padding: window.innerWidth <= 768 ? '16px' : '20px',
                      minHeight: 'calc(100vh - 64px)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <img 
                          src={winpadelLogo} 
                          alt="WinPadel" 
                          style={{ 
                            maxWidth: window.innerWidth <= 768 ? '200px' : '300px', 
                            height: 'auto', 
                            width: '100%',
                          }} 
                        />
                      </div>
                    </div>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            
            {/* Rutas para alumnos */}
            <Route
              path="/perfil"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <AlumnoPerfil />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mis-creditos"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <AlumnoCreditos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mi-historial-pagos"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <AlumnoHistorialPagos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mis-clases"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <AlumnoClases />
                </ProtectedRoute>
              }
            />
            <Route
              path="/solicitud-clase"
              element={
                <ProtectedRoute allowedRoles={['ALUMNO']}>
                  <AlumnoSolicitudClase />
                </ProtectedRoute>
              }
            />
            
            {/* Administración de Categorías */}
            <Route
              path="/administracion-categorias"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AdministracionCategorias />
                </ProtectedRoute>
              }
            />
            
            {/* Administración de Canchas */}
            <Route
              path="/administracion-canchas"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AdministracionCanchas />
                </ProtectedRoute>
              }
            />
            
            {/* Administración de Profesores */}
            <Route
              path="/administracion-profesores"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AdministracionProfesores />
                </ProtectedRoute>
              }
            />
            
            {/* Configuración del Bot */}
            <Route
              path="/configuracion-bot"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <ConfiguracionBot />
                </ProtectedRoute>
              }
            />
            <Route
              path="/asistencia-bot"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AsistenciaBot />
                </ProtectedRoute>
              }
            />
            
            {/* Administración de Planes */}
            <Route
              path="/administracion-planes"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AdministracionPlanes />
                </ProtectedRoute>
              }
            />
            
            {/* Administración de Alumnos */}
            <Route
              path="/administracion-alumnos"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <AdministracionAlumnos />
                </ProtectedRoute>
              }
            />
            
            {/* Agenda / Calendario */}
            <Route
              path="/agenda-calendario"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR', 'PROFESOR']}>
                  <AgendaCalendario />
                </ProtectedRoute>
              }
            />
            
            {/* Solicitudes de Nuevos Alumnos */}
            <Route
              path="/solicitudes-nuevos-alumnos"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <SolicitudesNuevosAlumnos />
                </ProtectedRoute>
              }
            />
            
            {/* Solicitudes de Clase de Alumnos */}
            <Route
              path="/solicitudes-clase-alumno"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <SolicitudesClaseAlumno />
                </ProtectedRoute>
              }
            />
            
            {/* Registro de Pagos */}
            <Route
              path="/registro-pagos"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <RegistroPagos />
                </ProtectedRoute>
              }
            />
            
            {/* Descuentos de Créditos */}
            <Route
              path="/descuentos-creditos"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <DescuentosCreditos />
                </ProtectedRoute>
              }
            />
            
            {/* Descuento Manual de Créditos */}
            <Route
              path="/descuento-manual"
              element={
                <ProtectedRoute allowedRoles={['ADMINISTRADOR']}>
                  <DescuentoManual />
                </ProtectedRoute>
              }
            />
            
            {/* Ruta por defecto - redirigir a login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </LoginProvider>
    </ConfigProvider>
  );
}

export default App;

